import type { Rect } from "./PanelContentRect.js";

/** 세로 스택 자식 — 위에서 아래로 누적 배치되는 한 요소의 선언. */
export interface StackChild {
  /** 결과 맵의 키 (요소 식별자). */
  readonly id: string;
  /** 고정 높이(px). `fill`이 true면 무시. */
  readonly height?: number;
  /** 고정 너비(px). 미지정 시 content 전체 폭. */
  readonly width?: number;
  /** 직전 요소(또는 상단)와의 간격(px). default 0. */
  readonly gapTop?: number;
  /** 가로 정렬 — "center"면 content 폭 기준 중앙(width 지정 필요). default "left". */
  readonly align?: "left" | "center";
  /** true면 남은 세로 공간 전부를 높이로 (스크롤 영역 등). 마지막 요소에 주로 사용. */
  readonly fill?: boolean;
}

/** 세로 스택 레이아웃 선언 (CSS flex column 축소판). json으로 외부화해 코드 없이 배치 조정. */
export interface VerticalStackSpec {
  readonly children: readonly StackChild[];
}

/**
 * 세로 스택 spec을 content 영역 안의 실좌표(id→Rect)로 변환한다 (Phaser·도메인 비의존 순수 함수).
 * 위에서 아래로 `gapTop` 간격을 두고 각 자식을 누적 배치하고, `fill`은 남은 세로 공간을, `align:"center"`는
 * content 폭 기준 가로 중앙정렬을 적용한다. 같은 패널을 쓰는 뷰가 배치 로직을 코드가 아닌 데이터로 선언하게 한다.
 *
 * `fill`은 남은 세로 공간을 차지하되 **뒤따르는 고정 자식들의 높이+gapTop을 남겨둔다** — fill을 중간에 두고
 * 마지막 자식을 하단에 고정하는 배치를 지원한다(예: title → scroll(fill) → 배수바 하단). fill이 마지막이면
 * 남길 공간이 0이라 기존 동작과 동일하다.
 *
 * @param content 배치 기준 영역 (보통 resolvePanelContentRect 결과 — 프레임 inset된 콘텐츠 영역)
 */
export function resolveVerticalStack(spec: VerticalStackSpec, content: Rect): Record<string, Rect> {
  const out: Record<string, Rect> = {};
  const bottom = content.y + content.height;
  let y = content.y;
  spec.children.forEach((child, i) => {
    y += child.gapTop ?? 0;
    const width = child.width ?? content.width;
    const x = child.align === "center" ? content.x + (content.width - width) / 2 : content.x;
    let height: number;
    if (child.fill) {
      // 뒤따르는 고정 자식들의 (height + gapTop) 합을 남겨 fill 뒤 하단 고정 배치를 지원.
      let rest = 0;
      for (let j = i + 1; j < spec.children.length; j++) {
        rest += (spec.children[j]!.gapTop ?? 0) + (spec.children[j]!.height ?? 0);
      }
      height = bottom - y - rest;
    } else {
      height = child.height ?? 0;
    }
    out[child.id] = { x, y, width, height };
    y += height;
  });
  return out;
}

/** 한 행(rect) 안에서 가로로 배치되는 요소 — 좌측 정렬 또는 우측 정렬. */
export interface RowItem {
  readonly id: string;
  /** 고정 너비(px). 좌측 요소에서 미지정 시 남은 폭 전부(우측 요소들 배치 후 잔여). */
  readonly width?: number;
  /** "left"=왼쪽부터, "right"=오른쪽부터 누적. */
  readonly align: "left" | "right";
  /** 직전(같은 방향) 요소와의 간격. default 0. */
  readonly gap?: number;
}

/**
 * 한 행 영역(rect) 안에서 좌/우 정렬로 요소를 가로 배치한다 (순수 함수).
 * 우측 요소를 오른쪽 끝에서부터 먼저 깔고, 좌측 요소를 왼쪽부터 채운다. 좌측 요소의 width 미지정 시
 * 남은 폭(우측 요소 영역 전까지) 전부를 차지한다. 세로(y/height)는 행 rect 그대로 — 행 내부 세로 분할은
 * 호출자가 각 결과 rect에 다시 resolveVerticalStack을 적용한다(stack→row→stack 조합).
 */
export function resolveRow(items: readonly RowItem[], rect: Rect): Record<string, Rect> {
  const out: Record<string, Rect> = {};
  let rightX = rect.x + rect.width;
  for (const item of items) {
    if (item.align !== "right") continue;
    rightX -= item.gap ?? 0;
    const w = item.width ?? 0;
    rightX -= w;
    out[item.id] = { x: rightX, y: rect.y, width: w, height: rect.height };
  }
  let leftX = rect.x;
  for (const item of items) {
    if (item.align !== "left") continue;
    leftX += item.gap ?? 0;
    const w = item.width ?? Math.max(0, rightX - leftX);
    out[item.id] = { x: leftX, y: rect.y, width: w, height: rect.height };
    leftX += w;
  }
  return out;
}

/** 2D 그리드 배치 사양 — cols×rows 균등 셀. */
export interface GridSpec {
  readonly cols: number;
  readonly rows: number;
  readonly colGap?: number;
  readonly rowGap?: number;
}

/**
 * rect를 cols×rows 균등 그리드로 나눈 각 셀의 rect 배열을 반환한다 (순수 함수).
 * index = row*cols + col (좌→우, 위→아래). 셀 내부 요소 배치는 호출자가 각 셀 rect 기준으로 처리.
 */
export function resolveGrid(spec: GridSpec, rect: Rect): Rect[] {
  const colGap = spec.colGap ?? 0;
  const rowGap = spec.rowGap ?? 0;
  const cellW = (rect.width - (spec.cols - 1) * colGap) / spec.cols;
  const cellH = (rect.height - (spec.rows - 1) * rowGap) / spec.rows;
  const out: Rect[] = [];
  for (let r = 0; r < spec.rows; r++) {
    for (let c = 0; c < spec.cols; c++) {
      out.push({
        x: rect.x + c * (cellW + colGap),
        y: rect.y + r * (cellH + rowGap),
        width: cellW,
        height: cellH,
      });
    }
  }
  return out;
}
